import React from 'react';

function PhilosophicalResources() {
  return (
    <div className="card">
      <div className="card-header">
        <h2>Philosophical Framework</h2>
        <p>Coping tools and perspectives for survivors</p>
      </div>
      <div className="card-body">
        <div className="philosophy-section">
          <h3>Core Principles</h3>
          <div className="principles-list">
            <div className="principle-item">
              <h4>Tell the truth</h4>
              <p>
                Facing violence and trauma requires honesty—with yourself and others. By documenting your 
                experiences, you are witnessing your truth and refusing to let it be erased or minimized.
                This act of truth-telling is both personal healing and a social good.
              </p>
            </div>
            
            <div className="principle-item">
              <h4>Help others awaken</h4>
              <p>
                When you speak your truth and seek protection, you create ripples that help others recognize 
                similar patterns in their own lives. Your courage can be the catalyst for someone else's awakening.
                Communities heal when individuals share their stories.
              </p>
            </div>
            
            <div className="principle-item">
              <h4>Preserve what beauty you can</h4>
              <p>
                Even amid trauma and violence, there is beauty worth preserving particularly in other relationships, 
                in small moments of kindness, in your own resilience. The act of preservation is itself 
                a form of resistance against violence and chaos.
              </p>
            </div>
            
            <div className="principle-item">
              <h4>Live as a witness, not as a victim</h4>
              <p>
                To witness means to see clearly and speak truthfully. To live consciously rather than be 
                manipulated by fear, denial, or others' narratives about your experience. By documenting 
                your truth, you reclaim agency and refuse to be a puppet to violence or intimidation.
              </p>
            </div>
          </div>
        </div>
        
        <div className="philosophy-section">
          <h3>Philosophical Perspectives</h3>
          
          <div className="perspective">
          <h4>"It is not things themselves that disturb us most, but our judgments about them."</h4>
            <p>
              While violence and trauma are real external events, our response to them is where we 
              can find agency. We cannot always control what happens to us, but we 
              can work to shape our responses.
              Documentation and safety planning are proactive practices used to acknowledge reality 
              while focusing on what actions are within your control. By documenting incidents 
              and creating safety plans, you practice the Stoic virtue of practical wisdom.
            </p>            <p>
            Similarly, Zen perspective reminds us that no external authority — not a partner, parent, or even 
              a counselor holds the final truth about your experience. The path to healing is deeply 
              personal and requires trusting your direct experience over others' interpretations of it.
              When someone dismisses, minimizes, or reinterprets your experience of violence, 
              remember that your direct experience is the truest authority. External validation 
              is helpful but not necessary for your truth to be real.
            </p>
          </div>
          
          <div className="perspective">
          <h4>"Enlightenment does not come by imagining figures of light, but by making the darkness conscious."</h4>
            <p>
              Healing comes not from denying darkness but from 
              consciously engaging with it. For survivors, this means acknowledging trauma 
              rather than suppressing it.
              The act of documentation is a form of "making the darkness conscious"—bringing 
              hidden experiences into awareness where they can be addressed. This process 
              helps integrate fragmented experiences into a coherent narrative.
            </p>
          </div>
        </div>
        
        <div className="philosophy-section">
          <h3>Practical Wisdom</h3>
          
          <div className="wisdom-quote">
            <p>
              This isn't about passive acceptance but active preparation. By documenting incidents, 
              creating safety plans, and seeking support, you develop the ability to face what comes 
              with clarity and readiness rather than being overwhelmed by it.
            </p>
          </div>
          
          <div className="wisdom-practices">
            <h4>Practices for Building Resilience:</h4>
            <ul>
              <li>
                <strong>Observation without identification:</strong> Notice thoughts and 
                feelings without being consumed by them
              </li>
              <li>
                <strong>Focus on what remains good:</strong> Maintain connection to beauty, 
                meaning, and relationships even amid difficulty
              </li>
              <li>
                <strong>Documentation as witness:</strong> Record experiences to maintain 
                clarity about what has happened
              </li>
              <li>
                <strong>Community as anchor:</strong> Connect with supportive others who can 
                help maintain perspective
              </li>
              <li>
                <strong>Breathing as foundation:</strong> Return to conscientious breathing as a source of 
                centering during emotional storms
              </li>
            </ul>
          </div>
        </div>
        
        <div className="philosophy-section">
          <h3>Self-awareness and Candor</h3>
          <p>
            Healing from violence requires honest self-assessment without self-blame. You can 
            acknowledge vulnerabilities and blindspots without taking responsibility for 
            another's choice to harm.
            The most reasonable approach isn't perfect prediction or prevention but cultivating 
            adaptability and resilience. The future will contain surprises regardless of how 
            carefully we watch for them. The question becomes: how do we develop systems 
            (personal and collective) that can bend without breaking when the unexpected occurs?
          </p>
        </div>
        
        <div className="philosophy-section conclusion">
          <h3>A Framework for Moving Forward</h3>
          <p>
            By combining documentation tools with philosophical resilience practices, this 
            application aims to support survivors in both practical protection and emotional 
            healing. The philosophical core serves as a foundation for the practical tools.
            As you use these tools, remember that documentation is not just for legal purposes—it's 
            also a practice of witnessing your own experience, an act of courage, and a step toward 
            both personal healing and collective change.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PhilosophicalResources;